import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { type InvoiceDto, type PaymentDto } from '@shared';
import { apiGet, apiPost, ApiError } from '../../lib/api';
import { useLegal } from '../../lib/legal-context';
import { mapPaymentError } from '../../lib/payment-errors';

// Client-side invoice detail. Phase 5d: Pay-by-card CTA, result-screen banner,
// status polling, payments timeline. Reads disclaimer copy via useLegal() so
// every payment-surface string flows from cmsLegal — REQ-008.

const STATUS_LABEL: Record<string, string> = {
  draft: 'Draft',
  issued: 'Outstanding — please pay by the due date',
  awaiting_wire: 'Bank transfer initiated',
  received: 'Funds received — awaiting reconciliation',
  cleared: 'Paid',
  void: 'Voided',
  refunded: 'Refunded',
};

type PayResult = 'success' | 'failed' | 'cancelled' | 'pending';

function isPayResult(s: string | null): s is PayResult {
  return s === 'success' || s === 'failed' || s === 'cancelled' || s === 'pending';
}

function formatMoney(amount_pence: number, currency: string): string {
  try {
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency, maximumFractionDigits: 0 })
      .format(amount_pence / 100);
  } catch {
    return `${currency} ${(amount_pence / 100).toLocaleString()}`;
  }
}

function formatTs(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso.slice(0, 16);
  return d.toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

export function PortalInvoiceDetail() {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const legal = useLegal();
  const params = new URLSearchParams(location.search);
  const payResultParam = params.get('pay_result');
  const payResult: PayResult | null = isPayResult(payResultParam) ? payResultParam : null;

  const [inv, setInv] = useState<InvoiceDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [paying, setPaying] = useState(false);
  const [payError, setPayError] = useState<string | null>(null);
  const [pollExhausted, setPollExhausted] = useState(false);
  const pollAttempts = useRef(0);

  // Initial load.
  useEffect(() => {
    if (!id) return;
    apiGet<{ invoice: InvoiceDto }>(`/api/invoices/${id}`)
      .then((r) => setInv(r.invoice))
      .catch((err: Error) => setError(err instanceof ApiError ? err.serverMessage : 'Could not load invoice.'));
  }, [id]);

  // Polling on `?pay_result=success`. Re-fetch until status flips to cleared
  // or we've burned 15 attempts (~30s). The webhook is the authoritative
  // state mover — the poll just lets the page reflect it without a refresh.
  useEffect(() => {
    if (payResult !== 'success' || !id) return;
    if (inv?.status === 'cleared') return;
    let stopped = false;
    const tick = async () => {
      if (stopped) return;
      pollAttempts.current += 1;
      try {
        const r = await apiGet<{ invoice: InvoiceDto }>(`/api/invoices/${id}`);
        if (stopped) return;
        setInv(r.invoice);
        if (r.invoice.status === 'cleared') {
          stopped = true;
          return;
        }
      } catch {
        // swallow — keep polling
      }
      if (pollAttempts.current < 15) {
        setTimeout(tick, 2000);
      } else {
        stopped = true;
        setPollExhausted(true);
      }
    };
    const timer = setTimeout(tick, 2000);
    return () => {
      stopped = true;
      clearTimeout(timer);
    };
  }, [id, payResult, inv?.status]);

  const handlePay = useCallback(async () => {
    if (!id) return;
    setPaying(true);
    setPayError(null);
    try {
      const r = await apiPost<{ payment_id: string; checkout_url: string; public_id: string }>(
        `/api/invoices/${id}/pay-card`,
        { consent_descriptor: true },
      );
      window.location.assign(r.checkout_url);
    } catch (err) {
      const code = err instanceof ApiError ? err.code : undefined;
      const fallback = err instanceof ApiError ? err.serverMessage : 'Could not start payment. Please try again.';
      setPayError(mapPaymentError(code, fallback));
      setPaying(false);
    }
  }, [id]);

  if (error) {
    return (
      <>
        <h1 className="portal-h1">Invoice not found</h1>
        <p className="portal-sub">{error}</p>
        <Link className="btn ghost" to="/portal/invoices" style={{ marginTop: 24 }}>← All invoices</Link>
      </>
    );
  }
  if (!inv) return <p className="portal-sub">Loading…</p>;

  const isOutstanding = inv.status === 'issued' || inv.status === 'awaiting_wire' || inv.status === 'received';
  const showPayCard = inv.status === 'issued' && (inv.pay_card_enabled ?? true);
  const showLegacyDisclaimerNotice =
    inv.disclaimers_version && legal.version && inv.disclaimers_version !== legal.version;

  return (
    <>
      <div style={{ fontSize: 13, color: 'var(--fg-mute)', marginBottom: 8 }}>
        <Link to="/portal/invoices" style={{ borderBottom: '1px solid var(--rule)' }}>Invoices</Link>
        {' / '}<span className="rid">{inv.id}</span>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 16, flexWrap: 'wrap' }}>
        <div>
          <h1 className="portal-h1">Invoice {inv.id}</h1>
          <p className="portal-sub">
            Proposal{' '}
            <Link to={`/portal/proposals/${inv.proposal_id}`} className="rid" style={{ borderBottom: '1px solid var(--rule)' }}>
              {inv.proposal_id}
            </Link>
            {inv.issued_at && <> · issued {inv.issued_at.slice(0, 10)}</>}
          </p>
        </div>
      </div>

      {payResult && (
        <ResultBanner
          result={payResult}
          status={inv.status}
          pollExhausted={pollExhausted}
          failureMessage={
            legal.payment_failure_disclaimer
              ?? 'Your card payment could not be completed. Please try again or pay by bank transfer.'
          }
        />
      )}

      <div className="proposal-form">
        <div className="card">
          <h3 className="proposal-section-h">Summary</h3>
          <div style={{ fontFamily: 'var(--font-display)', fontSize: 32, letterSpacing: '-0.02em', marginBottom: 6, fontVariantNumeric: 'tabular-nums' }}>
            {formatMoney(inv.amount_pence, inv.currency)}
          </div>
          <div style={{ color: 'var(--fg-soft)', fontSize: 14, marginBottom: 18 }}>
            {STATUS_LABEL[inv.status]}
          </div>
          {inv.milestone_label && (
            <div className="proposal-kv">
              <span>Milestone</span>
              <strong>{inv.milestone_label}</strong>
            </div>
          )}
          <div className="proposal-kv">
            <span>Due</span>
            <strong>{inv.due_at.slice(0, 10)}</strong>
          </div>
          {inv.paid_at && (
            <div className="proposal-kv">
              <span>Paid</span>
              <strong>{inv.paid_at.slice(0, 10)}</strong>
            </div>
          )}
          {showLegacyDisclaimerNotice && (
            <div className="proposal-kv">
              <span>Disclaimers</span>
              <strong>
                v{inv.disclaimers_version}{' '}
                <Link to="/legal" style={{ borderBottom: '1px solid var(--rule)', fontWeight: 400 }}>
                  view latest
                </Link>
              </strong>
            </div>
          )}
          {inv.notes && (
            <>
              <h4 className="proposal-sub-h">Notes</h4>
              <p className="proposal-prose">{inv.notes}</p>
            </>
          )}
        </div>

        {showPayCard && (
          <div className="card">
            <h3 className="proposal-section-h">Pay by card</h3>
            <p style={{ color: 'var(--fg-soft)', fontSize: 14, lineHeight: 1.6, marginBottom: 14 }}>
              Pay this invoice instantly via Revolut hosted checkout. We accept American Express,
              Visa, Mastercard and UnionPay.
            </p>
            <button
              type="button"
              className="btn primary"
              onClick={handlePay}
              disabled={paying}
              aria-busy={paying}
              style={{ minWidth: 180 }}
            >
              {paying ? 'Redirecting…' : `Pay ${formatMoney(inv.amount_pence, inv.currency)} by card`}
            </button>
            {payError && (
              <div role="alert" style={{ marginTop: 12, fontSize: 13, color: 'var(--danger, #b91c1c)' }}>
                {payError}
              </div>
            )}
            <p style={{ marginTop: 14, fontSize: 12, color: 'var(--fg-mute)', lineHeight: 1.6 }}>
              {legal.payment_descriptor
                ?? 'Charged as consultancy / research / introduction fees by Omega Trade and Finance Ltd. No client money is held by Omega.'}
            </p>
          </div>
        )}

        {isOutstanding && (
          <div className="card">
            <h3 className="proposal-section-h">{showPayCard ? 'Prefer bank transfer?' : 'How to pay'}</h3>
            <p style={{ color: 'var(--fg-soft)', fontSize: 14, lineHeight: 1.6, marginBottom: 14 }}>
              Bank transfer to the Omega Trade operating account, quoting the reference below so
              Finance can reconcile your payment.
            </p>
            <div className="invoice-ref-card">
              <div className="invoice-ref-label">Payment reference</div>
              <div className="invoice-ref">{inv.payment_reference}</div>
            </div>
            <h4 className="proposal-sub-h">Bank details</h4>
            <p className="proposal-prose">
              Beneficiary: <strong>Omega Trade and Finance Ltd</strong>
              <br />Bank: Revolut Business
              <br />Sort code / IBAN: contact <a href="mailto:finance@omegatrade.co.uk" style={{ borderBottom: '1px solid currentColor' }}>finance@omegatrade.co.uk</a> for current account details
              <br />Reference: <strong>{inv.payment_reference}</strong>
            </p>
          </div>
        )}

        {inv.payments && inv.payments.length > 0 && (
          <div className="card">
            <h3 className="proposal-section-h">Payments</h3>
            <PaymentsTimeline payments={inv.payments} currency={inv.currency} />
          </div>
        )}

        {inv.status === 'cleared' && (
          <div className="card">
            <h3 className="proposal-section-h">Receipt</h3>
            <p className="proposal-prose">
              {inv.received_amount_pence != null
                ? `Received ${formatMoney(inv.received_amount_pence, inv.currency)}`
                : 'Funds received'}
              {inv.payment_rail && ` via ${inv.payment_rail === 'card' ? 'card' : 'bank transfer'}`}
              {inv.paid_at && ` on ${inv.paid_at.slice(0, 10)}`}.
            </p>
            {inv.external_ref && (
              <div className="proposal-kv">
                <span>External reference</span>
                <strong style={{ fontFamily: 'var(--mono)', fontSize: 12 }}>{inv.external_ref}</strong>
              </div>
            )}
          </div>
        )}
      </div>

      {legal.refund_policy_summary && (
        <p style={{ marginTop: 18, fontSize: 12, color: 'var(--fg-mute)', lineHeight: 1.6, maxWidth: 720 }}>
          <strong style={{ color: 'var(--fg-soft)', fontWeight: 600 }}>Refunds. </strong>
          {legal.refund_policy_summary}
        </p>
      )}

      <div className="proposal-actions">
        <Link to="/portal/invoices" className="btn ghost">← Back to invoices</Link>
      </div>
    </>
  );
}

function ResultBanner({
  result,
  status,
  pollExhausted,
  failureMessage,
}: {
  result: PayResult;
  status: InvoiceDto['status'];
  pollExhausted: boolean;
  failureMessage: string;
}) {
  let tone: 'accent' | 'amber' | 'red' | 'neutral' = 'neutral';
  let message: string;

  if (result === 'success') {
    if (status === 'cleared') {
      tone = 'accent';
      message = 'Payment received — thank you. A receipt has been sent by email.';
    } else if (pollExhausted) {
      tone = 'amber';
      message = 'Still processing — a receipt will be emailed when Revolut confirms.';
    } else {
      tone = 'accent';
      message = 'Processing — confirming your payment with Revolut…';
    }
  } else if (result === 'pending') {
    tone = 'amber';
    message = 'Payment is processing. We’ll send a receipt by email when confirmed.';
  } else if (result === 'failed') {
    tone = 'red';
    message = failureMessage;
  } else {
    tone = 'neutral';
    message = 'You cancelled the payment. Your invoice is still outstanding.';
  }

  const palette: Record<typeof tone, { bg: string; fg: string; border: string }> = {
    accent: { bg: 'rgba(43,155,195,0.10)', fg: 'var(--accent, #2b9bc3)', border: 'rgba(43,155,195,0.35)' },
    amber: { bg: 'rgba(217,119,6,0.10)', fg: '#92400e', border: 'rgba(217,119,6,0.35)' },
    red: { bg: 'rgba(185,28,28,0.10)', fg: '#991b1b', border: 'rgba(185,28,28,0.35)' },
    neutral: { bg: 'var(--bg-soft, #f5f5f4)', fg: 'var(--fg, #1c1917)', border: 'var(--rule, #e7e5e4)' },
  };
  const c = palette[tone];

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        margin: '16px 0 8px',
        padding: '12px 16px',
        background: c.bg,
        color: c.fg,
        border: `1px solid ${c.border}`,
        borderRadius: 8,
        fontSize: 14,
        lineHeight: 1.5,
      }}
    >
      {message}
    </div>
  );
}

function PaymentsTimeline({ payments, currency }: { payments: PaymentDto[]; currency: string }) {
  const rows = [...payments].sort((a, b) => a.created_at.localeCompare(b.created_at));
  return (
    <table className="payments-timeline" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
      <thead>
        <tr style={{ textAlign: 'left', color: 'var(--fg-mute)', fontWeight: 500 }}>
          <th style={{ padding: '6px 8px' }}>Ref</th>
          <th style={{ padding: '6px 8px' }}>When</th>
          <th style={{ padding: '6px 8px' }}>Method</th>
          <th style={{ padding: '6px 8px' }}>Status</th>
          <th style={{ padding: '6px 8px', textAlign: 'right' }}>Amount</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((p) => (
          <PaymentRow key={p.id} p={p} currency={currency} />
        ))}
      </tbody>
    </table>
  );
}

function PaymentRow({ p, currency }: { p: PaymentDto; currency: string }) {
  const methodLabel =
    p.method === 'card'
      ? `Card via Revolut${p.card_network ? ` · ${p.card_network.toUpperCase()}` : ''}${p.card_last4 ? ` ····${p.card_last4}` : ''}`
      : 'Bank transfer';
  const statusLabel: Record<PaymentDto['status'], string> = {
    pending: 'Pending',
    succeeded: 'Succeeded',
    failed: 'Failed',
    refunded: 'Refunded',
  };
  const isRefunded = p.status === 'refunded';
  const isFailed = p.status === 'failed';
  return (
    <>
      <tr style={{ borderTop: '1px solid var(--rule, #e7e5e4)' }}>
        <td style={{ padding: '8px', fontFamily: 'var(--mono)', fontSize: 12 }}>{p.id}</td>
        <td style={{ padding: '8px', color: 'var(--fg-soft)' }}>{formatTs(p.created_at)}</td>
        <td style={{ padding: '8px' }}>{methodLabel}</td>
        <td style={{ padding: '8px' }}>
          <StatusPill status={p.status}>{statusLabel[p.status]}</StatusPill>
        </td>
        <td style={{ padding: '8px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
          {isRefunded ? `(${formatMoney(p.refund_amount_minor ?? p.amount_minor, currency)})` : formatMoney(p.amount_minor, currency)}
        </td>
      </tr>
      {isFailed && p.failure_message && (
        <tr>
          <td colSpan={5} style={{ padding: '0 8px 8px 8px', fontSize: 12, color: '#991b1b' }}>
            {p.failure_message}
          </td>
        </tr>
      )}
    </>
  );
}

function StatusPill({ status, children }: { status: PaymentDto['status']; children: React.ReactNode }) {
  const palette: Record<PaymentDto['status'], { bg: string; fg: string }> = {
    pending: { bg: 'rgba(217,119,6,0.10)', fg: '#92400e' },
    succeeded: { bg: 'rgba(22,163,74,0.12)', fg: '#166534' },
    failed: { bg: 'rgba(185,28,28,0.10)', fg: '#991b1b' },
    refunded: { bg: 'var(--bg-soft, #f5f5f4)', fg: 'var(--fg, #1c1917)' },
  };
  const c = palette[status];
  return (
    <span style={{
      display: 'inline-block',
      padding: '2px 8px',
      background: c.bg,
      color: c.fg,
      borderRadius: 999,
      fontSize: 12,
      fontWeight: 500,
    }}>
      {children}
    </span>
  );
}
